import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import {
	convertGeneratedTablesToHtml,
	convertTableContext,
} from '@core/util/documents';
import { FontInfo } from '@core/util/font';
import { ICON } from '@core/util/icon';
import { Font } from '@models/font';
import { ContextAccessors, FriendlyContextAccessors } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { ClipboardService } from '@services/clipboard/clipboard.service';
import { TableGeneratorService } from '@services/table-generator/table-generator.service';
import { FlattenedCustomTags, TagsService } from '@services/tags/tags.service';
import { BusinessState } from '@state/business';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { Editor } from 'tinymce';
import { addTable, reverseAddTable } from './table-generator.util';
import { Row, IAddTableProps } from './table-generator.model';
export interface TableGeneratorDialogData {
	html: string;
}

@UntilDestroy()
@Component({
	selector: 'app-table-generator-dialog',
	templateUrl: './table-generator-dialog.component.html',
	styleUrls: ['./table-generator-dialog.component.scss'],
	animations: [openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TableGeneratorDialogComponent implements OnInit {
	public contextOptions = Object.values(ContextAccessors)
		.filter((c) => c !== ContextAccessors.JURISDICTION_DEPARTMENTS)
		.map((context) => ({
			name: FriendlyContextAccessors[context],
			value: context,
			classes: ['capitalize'],
		}));
	public selectedContext: ContextAccessors;
	public FriendlyContextAccessors = FriendlyContextAccessors;

	public borderWidthOptions = [
		'0px',
		'0.5px',
		'1px',
		'1.5px',
		'2px',
		'2.5px',
		'3px',
		'6px',
	].map((width) => ({
		name: width,
		value: width,
	}));

	public columnWidths: number[] = [100];
	public headerRowHeight = 0;
	public bodyRowHeight = 0;
	public footerRowHeight = 0;

	public headerRow: Row = {
		cells: [{ text: '', colspan: 1 }],
	};

	public footerRows: Row[] = [
		{
			cells: [{ text: '', colspan: 1 }],
		},
	];

	public bodyRows: Row[] = [
		{
			cells: [{ text: '', colspan: 1 }],
		},
	];

	public numOfColumns = 1;

	public bgColor: string;
	public zebraStripe: boolean;
	public border: boolean;
	public borderColor: string;
	public borderWidth: string;
	public ICON = ICON;

	public selectedRow: 'header' | 'body' | 'footer' = 'body';
	public selectedRowIndex = -1;
	public selectedColumn = -1;
	public showHeader = true;
	public showFooter = true;
	public headerBG = true;

	public equalWidthColumns = true;
	public customHeights = false;
	public customHeaderHeight = false;
	public customBodyHeight = false;
	public customFooterHeight = false;

	//editor stuff
	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	public tmceInit: any;
	public editor: Editor;
	public showEditor = false;

	public tags: string[];
	public searchValue: string;
	public searchValues: string[];
	public leftColumnView: 'settings' | 'tags' = 'settings';
	public leftColumnOptions = [
		{ value: 'settings', label: 'Settings' },
		{ value: 'tags', label: 'Group tags' },
	];

	public editing = false;
	public previewHTML = '';

	public selectedContext$: BehaviorSubject<ContextAccessors> =
		new BehaviorSubject<ContextAccessors>(null);

	@SelectSnapshot(BusinessState.getDefaultFont) defaultFont: Font;
	@SelectSnapshot(BusinessState.getDefaultFontSize) defaultFontSize: number;
	@SelectSnapshot(BusinessState.getDefaultLineHeight) defaultLineHeight: number;
	@Select(TagsSelector.getTagInjectedDocument)
	parsedDocumentFilterFn$: Observable<(doc: string) => string>;

	constructor(
		private dialogRef: MatDialogRef<TableGeneratorDialogComponent>,
		private tagsService: TagsService,
		private clipboard: ClipboardService,
		private tableGeneratorService: TableGeneratorService,
		@Inject(MAT_DIALOG_DATA) private data: TableGeneratorDialogData,
		private cd: ChangeDetectorRef,
	) {}

	selectContext(
		selectedContext: DropdownResult<DropdownOption<ContextAccessors>>,
	): void {
		this.selectedContext$.next(selectedContext.result.value);
		this.selectedContext = selectedContext.result.value;
	}

	mergeRight(): void {
		const rows = {
			header: [this.headerRow],
			body: this.bodyRows,
			footer: this.footerRows,
		};
		this.tableGeneratorService.mergeRight(
			this.selectedRow,
			this.selectedColumn,
			this.numOfColumns,
			rows[this.selectedRow],
			this.selectedRowIndex,
		);

		this.updatePreviewHTML();
	}

	selectCell(
		row: 'header' | 'body' | 'footer',
		rowIndex: number,
		selectedColumn: number,
	): void {
		this.selectedRow = row;
		this.selectedRowIndex = rowIndex;
		this.selectedColumn = selectedColumn;
	}

	selectBorderWidth(
		selectedBorderWidth: DropdownResult<DropdownOption<string>>,
	): void {
		this.borderWidth = selectedBorderWidth.result.value;

		this.updatePreviewHTML();
	}

	updateNumberOfColumns(columns: number): void {
		this.cd.detach();

		const updatedTable = this.tableGeneratorService.updateNumberOfColumns(
			columns,
			this.numOfColumns,
			this.headerRow,
			this.bodyRows,
			this.footerRows,
			this.columnWidths,
		);

		this.headerRow = { cells: [] };
		this.bodyRows = [];
		this.footerRows = [];
		this.cd.reattach();
		this.cd.detectChanges();

		this.numOfColumns = updatedTable.numOfColumns;
		this.headerRow = { ...updatedTable.headerRow };
		this.bodyRows = [...updatedTable.bodyRows];
		this.footerRows = [...updatedTable.footerRows];
		this.columnWidths = updatedTable.columnWidths;

		this.cd.detectChanges();

		this.updatePreviewHTML();
	}

	trackByIndex(index: number) {
		return index;
	}

	trackByLabel(_index: number, item: FlattenedCustomTags) {
		return item.label;
	}

	updateCellText(
		text: string,
		type: 'header' | 'body' | 'footer',
		index: number,
		rowIndex?: number,
	) {
		if (type === 'header') {
			this.headerRow.cells[index].text = text;
		} else if (type === 'body') {
			this.bodyRows[rowIndex || 0].cells[index].text = text;
		} else if (type === 'footer') {
			this.footerRows[rowIndex || 0].cells[index].text = text;
		}

		this.updatePreviewHTML();
	}

	updateBodyRows(val: number): void {
		if (this.bodyRows.length < val) {
			this.addBodyRow();
		} else if (this.bodyRows.length > val) {
			this.bodyRows.splice(val);
		}

		this.updatePreviewHTML();
	}

	addBodyRow(): void {
		const row: Row = { cells: [] };
		for (let i = 0; i < this.numOfColumns; i++) {
			row.cells.push({ text: '', colspan: 1 });
		}

		this.bodyRows.push(row);
		this.updatePreviewHTML();
	}

	removeBodyRow(index: number): void {
		this.bodyRows.splice(index, 1);
		this.updatePreviewHTML();
	}

	updateFooterRows(val: number): void {
		if (this.footerRows.length < val) {
			this.addFooterRow();
		} else if (this.footerRows.length > val) {
			this.footerRows.splice(val);
		}
		this.updatePreviewHTML();
	}

	addFooterRow(): void {
		const row: Row = { cells: [] };
		for (let i = 0; i < this.numOfColumns; i++) {
			row.cells.push({ text: '', colspan: 1 });
		}

		this.footerRows.push(row);
		this.updatePreviewHTML();
	}

	removeFooterRow(index: number): void {
		this.footerRows.splice(index, 1);
		this.updatePreviewHTML();
	}

	toggleShowHeader(): void {
		this.showHeader = !this.showHeader;

		if (this.showHeader) {
			this.addHeaderRow();
		} else {
			this.removeHeaderRow();
		}
	}

	addHeaderRow(): void {
		const row: Row = { cells: [] };
		for (let i = 0; i < this.numOfColumns; i++) {
			row.cells.push({ text: '', colspan: 1 });
		}

		this.headerRow = row;
		this.updatePreviewHTML();
	}

	removeHeaderRow(): void {
		this.headerRow = { cells: [] };
		this.updatePreviewHTML();
	}

	addTable(): void {
		const props: IAddTableProps = {
			borderColor: this.borderColor,
			borderWidth: this.borderWidth,
			zebraStripe: this.zebraStripe,
			numOfColumns: this.numOfColumns,
			selectedContext: this.selectedContext,
			headerRow: this.headerRow,
			bodyRows: this.bodyRows,
			footerRows: this.footerRows,
			columnWidths: this.columnWidths,
			bgColor: this.bgColor !== '#ffffff' ? this.bgColor : undefined,
			showHeader: this.showHeader,
			headerBG: this.headerBG,
		};

		if (this.customHeights) {
			if (this.customHeaderHeight) {
				props.headerHeight = this.headerRowHeight;
			}

			if (this.customBodyHeight) {
				props.bodyHeight = this.bodyRowHeight;
			}

			if (this.customFooterHeight) {
				props.footerHeight = this.footerRowHeight;
			}
		}

		const table = addTable(props);
		this.dialogRef.close(table);
	}

	reverseAddTable(html: string): void {
		const result = reverseAddTable(html);

		this.zebraStripe = result.zebraStripe;
		this.border = result.border;
		this.borderColor = result.borderColor;
		this.bgColor = result.bgColor;
		this.borderWidth = result.borderWidth;
		this.showHeader = result.showHeader;
		this.headerRow = result.headerRow;
		this.bodyRows = result.bodyRows;
		this.numOfColumns = result.numOfColumns;
		this.footerRows = result.footerRows;
		if (this.footerRows.length > 0) {
			this.showFooter = true;
		} else {
			this.showFooter = false;
		}
		this.selectedContext = this.getContextValue(result.selectedContext);
		this.selectedContext$.next(this.selectedContext);
		this.columnWidths = result.columnWidths;
		const allSame = this.columnWidths.every(
			(width) => width === this.columnWidths[0],
		);
		if (allSame) {
			this.equalWidthColumns = true;
		} else {
			this.equalWidthColumns = false;
		}
		this.headerBG = result.headerBG;

		if (result.headerHeight || result.bodyHeight || result.footerHeight) {
			this.customHeights = true;
			if (result.headerHeight) {
				this.customHeaderHeight = true;
				this.headerRowHeight = result.headerHeight;
			}

			if (result.bodyHeight) {
				this.customBodyHeight = true;
				this.bodyRowHeight = result.bodyHeight;
			}

			if (result.footerHeight) {
				this.customFooterHeight = true;
				this.footerRowHeight = result.footerHeight;
			}
		}

		this.updatePreviewHTML();
	}

	getContextValue(friendlyContext: string): ContextAccessors {
		// The match here is not exact because of escaped characters, so as long as the text contaions a substring of the name, it will match
		// This is just a workaround after this broke because of the editor update, we shouldn't be using a innerText to determine the context, attr.data makes more sense for this.
		const context = this.contextOptions.find((option) =>
			friendlyContext
				.toLowerCase()
				.includes(option.name.replace(/[\s-]/g, '')?.toLowerCase()),
		)?.value;

		return context as ContextAccessors;
	}

	handleEditorFocus(id: string) {
		if (typeof id !== 'string') {
			return;
		}

		const splits = id.split('-');
		if (splits.length > 1 && ['header', 'body', 'footer'].includes(splits[0])) {
			if (splits[0] === 'header') {
				this.selectCell('header', 0, Number(splits[1]));
			} else {
				this.selectCell(
					splits[0] as 'body' | 'footer',
					Number(splits[1]),
					Number(splits[2]),
				);
			}
		}
	}

	getLabelId(label: string): string {
		return label.replace(/\./g, '');
	}

	searchResultsLabel(label: string): string {
		return label.replace(this.searchValue, `<b>${this.searchValue}</b>`);
	}

	copyToClipboard(text: string) {
		this.clipboard.copyText(text, true);
	}

	searchValueChanged(value: string): void {
		this.searchValue = value;
		const searchValueLower = value.toLowerCase();

		this.searchValues = this.tags.filter((tag) =>
			tag.toLowerCase().includes(searchValueLower),
		);
	}

	updatePreviewHTML(): void {
		const props: IAddTableProps = {
			borderColor: this.borderColor,
			borderWidth: this.borderWidth,
			zebraStripe: this.zebraStripe,
			numOfColumns: this.numOfColumns,
			selectedContext: this.selectedContext,
			headerRow: this.headerRow,
			bodyRows: this.bodyRows,
			footerRows: this.footerRows,
			columnWidths: this.columnWidths,
			bgColor: this.bgColor !== '#ffffff' ? this.bgColor : undefined,
			showHeader: this.showHeader,
			headerBG: this.headerBG,
			forPreview: true,
		};

		if (this.customHeights) {
			if (this.customHeaderHeight) {
				props.headerHeight = this.headerRowHeight;
			}

			if (this.customBodyHeight) {
				props.bodyHeight = this.bodyRowHeight;
			}

			if (this.customFooterHeight) {
				props.footerHeight = this.footerRowHeight;
			}
		}
		let html = addTable(props);

		html = convertGeneratedTablesToHtml(html);
		this.injectTagsIntoHtml(cloneDeep(html))
			.pipe(take(1))
			.subscribe((result) => {
				this.previewHTML = result;
			});
	}

	injectTagsIntoHtml(html: string): Observable<string> {
		return this.parsedDocumentFilterFn$.pipe(
			untilDestroyed(this),
			map((filter) => filter(html)),
		);
	}

	updateEqualColumnWidths() {
		if (this.equalWidthColumns) {
			const width = 100 / this.numOfColumns;
			this.columnWidths = Array(this.numOfColumns).fill(width);
		}

		this.updatePreviewHTML();
	}

	updateRowHeights() {
		if (this.customHeights) {
			// TODO: Implement custom row heights logic
		}
	}

	closeModal(): void {
		this.dialogRef.close();
	}

	ngOnInit(): void {
		this.selectedContext$.pipe(untilDestroyed(this)).subscribe((context) => {
			if (context) {
				this.tagsService
					.getContextTags(convertTableContext(context), true)
					.pipe(take(1))
					.subscribe((tags) => {
						this.tags = tags.map(
							(x) => `{{${this.searchResultsLabel(x.label)}}}`,
						);
						this.searchValues = cloneDeep(this.tags);
					});
			}
		});

		if (this.data.html) {
			this.reverseAddTable(this.data.html);
			this.editing = true;
		}

		// this.columns = "<table style='width:100%'><tr><td></td><tr></table>";
		this.tmceInit = {
			setup: (editor) => {
				editor.on('focus', (evt: FocusEvent) => {
					this.handleEditorFocus((evt.target as Element)?.id);
				});
			},
			advlist_number_styles:
				'default,lower-alpha,lower-roman,upper-alpha,upper-roman',
			base_url: '/tinymce',
			branding: false,
			columns_style: `
			@import url('https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,400;0,700;1,400;1,700&display=swap');
			@font-face {
				font-family: 'calibri';
				src: url('/assets/fonts/Calibri.woff2') format('woff2'), url('/assets/fonts/Calibri.woff') format('woff');
			}
			* { font-family: ${FontInfo[this.defaultFont].editorFont}; }
			p, li, td, th { font-size: ${this.defaultFontSize}pt; line-height: ${this.defaultLineHeight}; }
			h1 { font-size: 21pt; }
			h2 { font-size: 18pt; }
			h3 { font-size: 16pt; }
			h4 { font-size: 14pt; }
			h5 { font-size: 12pt; }
			h6 { font-size: 12pt; }
			.mceNonEditable { background-color: rgb(241, 255, 248); color: rgb(21, 132, 73); font-size: calc(1em - 2px); padding: 1px 3px; }
			table { margin-top: -6px; margin-bottom: -6px; }
			img { display: inline-block; height: auto; max-width: 100% }
			thead { background-color: #f3f3f3; }
			th { text-align: left; }
			td { vertical-align: top; }
			.table_zebra_striped tbody tr:nth-child(even) { background-color: #f6f6f6; }
			`,
			contextmenu:
				'undo redo | copy paste pastetext | lists| outdent indent | bold italic forecolor | removeformat',
			elementpath: false,
			fix_list_elements: true,
			font_size_formats:
				'5pt 6pt 7pt 8pt 9pt 10pt 11pt 12pt 13pt 14pt 15pt 16pt 17pt 18pt 19pt 20pt 21pt 22pt 23pt 24pt 36pt 48pt',
			forced_root_block: 'p',
			formats: {
				alignleft: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'left' },
				},
				aligncenter: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'center' },
				},
				alignright: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'right' },
				},
			},
			height: '100%',
			highlight_on_focus: false,
			image_advtab: false,
			image_uploadtab: true,
			images_file_types: 'jpeg,jpg,jpe,png,gif',
			images_reuse_filename: true,
			indent_use_margin: true,
			inline: true,
			fixed_toolbar_container: '#mytoolbar',
			line_height_formats: '1 1.1 1.15 1.2 1.3 1.4 1.5 1.6 2 2.5 3.0',
			menubar: false,
			pagebreak_separator: '<span class="pdf-pagebreak-before">&nbsp;</span>',
			pagebreak_split_block: true,
			paste_block_drop: true,
			paste_data_images: true,
			plugins: 'lists advlist image table code pagebreak',
			resize: false,
			statusbar: false,
			suffix: '.min',
			table_advtab: true,
			table_appearance_options: true,
			table_border_styles: [
				{ title: 'Solid', value: 'solid' },
				{ title: 'Inset', value: 'inset' },
				{ title: 'Double', value: 'double' },
				{ title: 'Outset', value: 'outset' },
				{ title: 'None', value: 'none' },
				{ title: 'Hidden', value: 'hidden' },
			],
			table_border_widths: [
				{ title: '1px', value: '1px' },
				{ title: '2px', value: '2px' },
				{ title: '3px', value: '3px' },
				{ title: '4px', value: '4px' },
				{ title: '5px', value: '5px' },
			],
			table_cell_advtab: true,
			table_class_list: [
				{
					title: 'None',
					value: 'table_no_style',
				},
				{
					title: 'Zebra striped',
					value: 'table_zebra_striped',
				},
			],
			table_default_attributes: {},
			table_default_styles: {
				border: 'none',
				width: '100%',
			},
			table_header_type: 'sectionCells',
			table_row_advtab: false,
			table_sizing_mode: 'relative',
			table_style_by_css: true,
			table_use_colgroups: false,
			text_patterns: [
				{ start: '*', end: '*', format: 'italic' },
				{ start: '**', end: '**', format: 'bold' },
				{ start: '#', format: 'h1', trigger: 'space' },
				{ start: '##', format: 'h2', trigger: 'space' },
				{ start: '###', format: 'h3', trigger: 'space' },
				{ start: '####', format: 'h4', trigger: 'space' },
				{ start: '#####', format: 'h5', trigger: 'space' },
				{ start: '######', format: 'h6', trigger: 'space' },
				{ start: '1.', cmd: 'InsertOrderedList', trigger: 'space' },
				{ start: '*', cmd: 'InsertUnorderedList', trigger: 'space' },
				{ start: '-', cmd: 'InsertUnorderedList', trigger: 'space' },
				{ start: '---', cmd: 'InsertHorizontalRule', trigger: 'space' },
				{ start: '--', replacement: '—' },
				{ start: '(c)', replacement: '©' },
			],
			toolbar: [
				{
					items: ['undo', 'redo'],
					name: 'history',
				},
				{
					items: ['fontsize'],
					name: 'font',
				},
				{
					items: ['blocks', 'forecolor'],
					name: 'fontLook',
				},
				{
					items: ['bold', 'italic', 'underline', 'strikethrough'],
					name: 'style',
				},
				{
					items: ['removeformat'],
					name: 'override',
				},
				{
					items: ['lineheight'],
					name: 'adjustment',
				},
				{
					items: ['alignleft', 'aligncenter', 'alignright', 'alignjustify'],
					name: 'alignment',
				},
				{
					items: ['bullist', 'numlist', 'table', 'image'],
					name: 'extra',
				},
				{
					items: ['hr'],
					name: 'page',
				},
				{
					items: ['outdent', 'indent'],
					name: 'format',
				},
				{
					items: ['code'],
					name: 'code',
				},
			],
			toolbar_mode: 'sliding',
		};
	}
}
